/**
 * The art-hash index: the file that turns a fingerprint into an artwork.
 *
 * ADR 0004 decided this lives outside SQL - a B-tree cannot accelerate Hamming
 * distance, and 52k fingerprints are small enough to search by brute force in
 * the time a network round trip would take. This module is that decision in
 * code, and it is deliberately the *only* definition of the format: the builder
 * imports `encodeArtIndex`, the app imports `decodeArtIndex` and
 * `searchArtIndex`, so there is no second writer that could drift.
 *
 * Layout, little-endian, no padding:
 *
 * ```text
 *   0   magic            "CSARTIDX"   8 bytes
 *   8   format version   uint16
 *   10  bits per hash    uint16
 *   12  entry count      uint32
 *   16  fingerprints     count x bits/8 bytes, entry order
 *       illustration ids count x 16 bytes, entry order
 * ```
 *
 * The two blocks are stored column-wise rather than interleaved so the whole
 * fingerprint block is one contiguous array: the search walks it without
 * touching an id, which is both faster and the reason the decoder can hand the
 * block out as a view instead of copying it.
 *
 * The illustration id is stored as its raw 16 bytes, not its 36-character
 * string: 20 bytes per entry cheaper, and `illustration_id` is the one key in
 * this whole design that is stable across reprints *and* across catalog
 * rebuilds, so an index built today keeps meaning something after the catalog
 * is reloaded. That is worth half a megabyte over storing a database row id.
 *
 * The artifact is one entry per illustration, and the format refuses the same
 * illustration twice - which is what makes the runner-up in a search a
 * *different* artwork rather than another row of the same one, and therefore
 * what makes the margin between them a usable measure of confidence.
 */
import { PHASH_BITS, defaultThreshold, similarityFromDistance } from './phash.js';
export const ART_INDEX_MAGIC = 'CSARTIDX';
export const ART_INDEX_FORMAT_VERSION = 1;
export const ART_INDEX_HEADER_BYTES = 16;
export const ART_INDEX_ID_BYTES = 16;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
function uuidToBytes(illustrationId) {
    const value = illustrationId.trim().toLowerCase();
    if (!UUID_PATTERN.test(value)) {
        throw new Error(`"${illustrationId}" is not a uuid, so it cannot be stored in the art index`);
    }
    const compact = value.replace(/-/g, '');
    const bytes = new Uint8Array(ART_INDEX_ID_BYTES);
    for (let index = 0; index < bytes.length; index += 1) {
        bytes[index] = Number.parseInt(compact.slice(index * 2, index * 2 + 2), 16);
    }
    return bytes;
}
function bytesToUuid(bytes, offset) {
    let compact = '';
    for (let index = 0; index < ART_INDEX_ID_BYTES; index += 1) {
        compact += (bytes[offset + index] ?? 0).toString(16).padStart(2, '0');
    }
    return [
        compact.slice(0, 8),
        compact.slice(8, 12),
        compact.slice(12, 16),
        compact.slice(16, 20),
        compact.slice(20),
    ].join('-');
}
const POPCOUNT = (() => {
    const table = new Uint8Array(256);
    for (let value = 1; value < 256; value += 1)
        table[value] = (value & 1) + (table[value >> 1] ?? 0);
    return table;
})();
/**
 * Encode an index.
 *
 * Entries are sorted by illustration id, so the same catalogue always produces
 * the same bytes no matter what order the rows arrived in - that is what makes
 * "rebuild and compare the checksum" a meaningful check.
 */
export function encodeArtIndex(entries, options = {}) {
    const bits = options.bits ?? PHASH_BITS;
    const hashBytes = bits / 8;
    if (!Number.isInteger(hashBytes))
        throw new Error(`bits must be a multiple of 8, got ${bits}`);
    const sorted = [...entries].sort((a, b) => (a.illustrationId < b.illustrationId ? -1 : a.illustrationId > b.illustrationId ? 1 : 0));
    const ids = sorted.map((entry) => entry.illustrationId.trim().toLowerCase());
    for (let index = 0; index < ids.length; index += 1) {
        if (index > 0 && ids[index] === ids[index - 1]) {
            throw new Error(`illustration ${ids[index]} appears in the art index twice`);
        }
    }
    const bytes = new Uint8Array(ART_INDEX_HEADER_BYTES + ids.length * (hashBytes + ART_INDEX_ID_BYTES));
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    for (let index = 0; index < ART_INDEX_MAGIC.length; index += 1) {
        bytes[index] = ART_INDEX_MAGIC.charCodeAt(index);
    }
    view.setUint16(8, options.formatVersion ?? ART_INDEX_FORMAT_VERSION, true);
    view.setUint16(10, bits, true);
    view.setUint32(12, ids.length, true);
    let hashOffset = ART_INDEX_HEADER_BYTES;
    for (let index = 0; index < sorted.length; index += 1) {
        const hash = sorted[index]?.hash;
        if (!hash || hash.length !== hashBytes) {
            throw new Error(`every art index hash must be ${hashBytes} bytes (${bits} bits), got ${hash?.length ?? 0}`);
        }
        bytes.set(hash, hashOffset);
        hashOffset += hashBytes;
    }
    let idOffset = hashOffset;
    for (let index = 0; index < ids.length; index += 1) {
        bytes.set(uuidToBytes(ids[index] ?? ''), idOffset);
        idOffset += ART_INDEX_ID_BYTES;
    }
    return bytes;
}
export function decodeArtIndex(bytes) {
    if (bytes.length < ART_INDEX_HEADER_BYTES) {
        throw new Error(`art index is ${bytes.length} bytes, too short to hold a ${ART_INDEX_HEADER_BYTES}-byte header`);
    }
    const magic = new TextDecoder().decode(bytes.subarray(0, ART_INDEX_MAGIC.length));
    if (magic !== ART_INDEX_MAGIC) {
        throw new Error(`not an art index: expected magic "${ART_INDEX_MAGIC}", found "${magic}"`);
    }
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const formatVersion = view.getUint16(8, true);
    if (formatVersion !== ART_INDEX_FORMAT_VERSION) {
        throw new Error(`art index format version ${formatVersion} is not supported (this build reads ${ART_INDEX_FORMAT_VERSION})`);
    }
    const bits = view.getUint16(10, true);
    const hashBytes = bits / 8;
    if (!Number.isInteger(hashBytes) || hashBytes < 1) {
        throw new Error(`art index declares ${bits} bits, which is not a multiple of 8`);
    }
    const count = view.getUint32(12, true);
    const expected = ART_INDEX_HEADER_BYTES + count * (hashBytes + ART_INDEX_ID_BYTES);
    if (bytes.length !== expected) {
        throw new Error(`art index is truncated: ${count} entries need ${expected} bytes, the file holds ${bytes.length}`);
    }
    const hashes = bytes.subarray(ART_INDEX_HEADER_BYTES, ART_INDEX_HEADER_BYTES + count * hashBytes);
    const illustrationIds = [];
    const idBase = ART_INDEX_HEADER_BYTES + count * hashBytes;
    for (let index = 0; index < count; index += 1) {
        illustrationIds.push(bytesToUuid(bytes, idBase + index * ART_INDEX_ID_BYTES));
    }
    return { formatVersion, bits, count, hashes, illustrationIds };
}
function distanceAt(index, position, probe, hashBytes) {
    const base = position * hashBytes;
    let distance = 0;
    for (let byte = 0; byte < hashBytes; byte += 1) {
        distance += POPCOUNT[(index.hashes[base + byte] ?? 0) ^ (probe[byte] ?? 0)] ?? 0;
    }
    return distance;
}
/**
 * Find the artwork a fingerprint belongs to.
 *
 * Exhaustive by design (ADR 0004): every entry is compared, because a linear
 * scan of a few hundred kilobytes costs less than the network round trip a
 * tree-shaped index would need to consult. The two nearest entries are tracked
 * because the *gap* between them is the confidence signal - if two artworks are
 * equally close, the fingerprint has not distinguished them.
 */
export function searchArtIndex(index, probe, options = {}) {
    const hashBytes = index.bits / 8;
    if (probe.length !== hashBytes) {
        throw new Error(`probe is ${probe.length * 8} bits but the index holds ${index.bits}`);
    }
    const threshold = options.threshold ?? defaultThreshold(index.bits);
    const limit = Math.max(1, options.limit ?? 5);
    let best = null;
    let runnerUp = null;
    const within = [];
    for (let position = 0; position < index.count; position += 1) {
        const distance = distanceAt(index, position, probe, hashBytes);
        const illustrationId = index.illustrationIds[position] ?? '';
        if (best === null || distance < best.distance) {
            if (best !== null)
                runnerUp = best;
            best = { illustrationId, distance, similarity: similarityFromDistance(distance, index.bits) };
        }
        else if (distance < (runnerUp?.distance ?? Number.POSITIVE_INFINITY) && illustrationId !== best.illustrationId) {
            runnerUp = { illustrationId, distance, similarity: similarityFromDistance(distance, index.bits) };
        }
        if (distance <= threshold) {
            within.push({ illustrationId, distance, similarity: similarityFromDistance(distance, index.bits) });
        }
    }
    within.sort((a, b) => a.distance - b.distance);
    return {
        bits: index.bits,
        threshold,
        matched: best !== null && best.distance <= threshold,
        best,
        runnerUp,
        marginBits: best === null ? index.bits : runnerUp ? runnerUp.distance - best.distance : index.bits,
        candidates: within.slice(0, limit),
        considered: index.count,
    };
}
//# sourceMappingURL=art-index.js.map